import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'

/**
 * Project-scoped history-scan control: a "Сканировать историю" button + period
 * picker modal + live progress banner. History scan runs per project (not
 * globally), so this is only rendered inside a project context (Sources tab and
 * the per-project Leads view).
 *
 * The scan runs cross-process (LeadScanJob in the bot process). This polls
 * ['history-scan', projectId] — the same key the Sources tab uses — so every
 * mount of the control reflects the same live scan state.
 */
export function HistoryScanControl({
  projectId,
  aiAvailable,
}: {
  projectId: number
  aiAvailable: boolean
}) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [days, setDays] = useState(7)
  const [customStartDate, setCustomStartDate] = useState('')
  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<number>>(new Set())
  const [showStatus, setShowStatus] = useState(false)
  const sourceSeededRef = useRef(false)
  const { data: sourcesData } = useQuery({
    queryKey: ['sources', projectId],
    queryFn: () => api.getSources(projectId),
    enabled: !!projectId && aiAvailable,
  })
  const { data: activeScan } = useQuery({
    queryKey: ['history-scan', projectId],
    queryFn: () => api.getActiveHistoryScan(projectId),
    enabled: !!projectId && aiAvailable,
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'running' || s === 'requested' || s === 'cancel_requested' ? 2500 : false
    },
  })
  const scanning =
    activeScan?.status === 'running' ||
    activeScan?.status === 'requested' ||
    activeScan?.status === 'cancel_requested'
  const stopping = activeScan?.status === 'cancel_requested'
  const scanSources = useMemo(
    () => (sourcesData?.items ?? []).filter((source) => !source.removed_at),
    [sourcesData?.items],
  )
  const selectedSourceIdList = Array.from(selectedSourceIds)
  const historyScanBody = {
    ...(customStartDate ? { start_date: customStartDate } : { days }),
    source_state_ids: selectedSourceIdList,
  }
  const startScan = useMutation({
    mutationFn: () => api.startHistoryScan(projectId, historyScanBody),
    onSuccess: (job) => {
      setShowStatus(true)
      queryClient.setQueryData(['history-scan', projectId], job)
      setOpen(false)
    },
  })
  const cancelScan = useMutation({
    mutationFn: () => api.cancelHistoryScan(projectId),
    onSuccess: (job) => {
      setShowStatus(true)
      queryClient.setQueryData(['history-scan', projectId], job)
    },
  })
  const prevStatus = useRef<string | undefined>(undefined)
  useEffect(() => {
    setShowStatus(false)
    prevStatus.current = undefined
  }, [projectId])
  useEffect(() => {
    if (scanning) setShowStatus(true)
  }, [scanning])
  useEffect(() => {
    if (activeScan?.status === 'done' || activeScan?.status === 'failed' || activeScan?.status === 'canceled') {
      setShowStatus(true)
    }
  }, [activeScan?.status])
  useEffect(() => {
    if (open && !sourceSeededRef.current && scanSources.length > 0) {
      const unscanned = scanSources.filter((source) => !source.last_scan_at)
      setSelectedSourceIds(
        new Set((unscanned.length > 0 ? unscanned : scanSources).map((source) => source.id)),
      )
      sourceSeededRef.current = true
    }
  }, [open, scanSources])
  useEffect(() => {
    const s = activeScan?.status
    const prev = prevStatus.current
    if (
      (prev === 'running' || prev === 'requested' || prev === 'cancel_requested') &&
      (s === 'done' || s === 'failed' || s === 'canceled')
    ) {
      void queryClient.invalidateQueries({ queryKey: ['sources', projectId] })
      void queryClient.invalidateQueries({ queryKey: ['leads', projectId] })
    }
    prevStatus.current = s
  }, [activeScan?.status, projectId, queryClient])

  if (!aiAvailable) return null

  function defaultScanSourceIds() {
    const unscanned = scanSources.filter((source) => !source.last_scan_at)
    return new Set((unscanned.length > 0 ? unscanned : scanSources).map((source) => source.id))
  }

  function openDialog() {
    sourceSeededRef.current = scanSources.length > 0
    setSelectedSourceIds(defaultScanSourceIds())
    setOpen(true)
  }

  function toggleSource(sourceId: number) {
    setSelectedSourceIds((prev) => {
      const next = new Set(prev)
      if (next.has(sourceId)) next.delete(sourceId)
      else next.add(sourceId)
      return next
    })
  }

  function selectAllSources() {
    setSelectedSourceIds(new Set(scanSources.map((source) => source.id)))
  }

  function selectUnscannedSources() {
    setSelectedSourceIds(
      new Set(scanSources.filter((source) => !source.last_scan_at).map((source) => source.id)),
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={openDialog}
        disabled={scanning || !projectId}
        className="btn btn--primary btn--sm disabled:opacity-40"
        style={{ alignSelf: 'flex-start' }}
        title="Просканировать историю источников проекта и найти лиды"
      >
        <span key={scanning ? 'busy' : 'idle'}>
          {scanning ? 'Идёт сканирование' : 'Сканировать историю'}
        </span>
      </button>

      {scanning && activeScan && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            background: 'var(--accent-soft)',
            border: '1px solid color-mix(in oklab, var(--accent) 30%, transparent)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
            color: 'var(--accent-ink)',
          }}
        >
          <span
            className="animate-spin"
            style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent', display: 'inline-block' }}
          />
          <span
            key={`${activeScan.status}-${(activeScan.result as { phase?: string } | null)?.phase ?? 'x'}-${activeScan.progress_done}-${activeScan.progress_total}`}
          >
            {stopping
              ? 'Останавливаю сканирование'
              : (activeScan.result as { phase?: string } | null)?.phase === 'ai'
                ? 'AI-анализ истории'
                : 'Сканирование истории'}
            {activeScan.progress_total > 0
              ? `: ${activeScan.progress_done}/${activeScan.progress_total}`
              : ' запускается…'}
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={stopping || cancelScan.isPending}
            onClick={() => cancelScan.mutate()}
            style={{ marginLeft: 'auto', background: 'var(--surface)' }}
          >
            {stopping || cancelScan.isPending ? 'Остановка...' : 'Остановить'}
          </button>
        </div>
      )}
      {showStatus && activeScan?.status === 'failed' && (
        <div
          style={{ padding: '10px 14px', background: 'var(--danger-soft)', border: '1px solid var(--danger-line)', borderRadius: 'var(--r-md)', fontSize: 13, color: 'var(--danger-ink)' }}
        >
          Сканирование не удалось: {activeScan.error || 'неизвестная ошибка'}
        </div>
      )}
      {showStatus && activeScan?.status === 'canceled' && (
        <div
          style={{ padding: '10px 14px', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', fontSize: 13, color: 'var(--ink-2)' }}
        >
          Сканирование остановлено.
        </div>
      )}
      {!scanning && showStatus && activeScan?.status === 'done' &&
        typeof (activeScan.result as { leads_found?: number } | null)?.leads_found === 'number' && (
          <div
            style={{ padding: '10px 14px', background: 'var(--teal-soft)', border: '1px solid color-mix(in oklab, var(--teal) 35%, transparent)', borderRadius: 'var(--r-md)', fontSize: 13, color: 'var(--teal-ink)' }}
          >
            Скан истории завершён: найдено лидов —{' '}
            <b>{(activeScan.result as { leads_found?: number }).leads_found}</b>.
          </div>
        )}

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(11,15,20,0.45)', display: 'grid', placeItems: 'center', zIndex: 80 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(560px, 92vw)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-3)', padding: '20px 22px' }}
          >
            <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 4px', color: 'var(--ink)' }}>
              Сканировать историю
            </h2>
            <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: '0 0 16px', lineHeight: 1.5 }}>
              Прочитаю историю источников проекта за период и найду лиды через AI. Списывается из квоты сканов истории.
            </p>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', marginBottom: 7 }}>
              Период
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              {([[7, '7 дней'], [30, '30 дней'], [90, '90 дней']] as const).map(([d, l]) => (
                <button
                  key={d}
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    setDays(d)
                    setCustomStartDate('')
                  }}
                  style={!customStartDate && days === d ? { background: 'var(--accent-soft)', color: 'var(--accent-ink)', borderColor: 'color-mix(in oklab, var(--accent) 40%, transparent)' } : undefined}
                >
                  {l}
                </button>
              ))}
            </div>
            <label style={{ display: 'grid', gap: 6, marginBottom: 18 }}>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>С конкретной даты</span>
              <input
                type="date"
                value={customStartDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setCustomStartDate(e.target.value)}
                style={{
                  height: 40,
                  borderRadius: 'var(--r-md)',
                  border: '1px solid var(--line)',
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  padding: '0 12px',
                  fontSize: 14,
                }}
              />
            </label>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)' }}>
                  Источники ({selectedSourceIds.size}/{scanSources.length})
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={selectAllSources}>
                    Все
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={selectUnscannedSources}
                    disabled={!scanSources.some((source) => !source.last_scan_at)}
                    title="Выбрать только источники, историю которых SENSOR ещё не сканировал"
                  >
                    Только без скана
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setSelectedSourceIds(new Set())}
                  >
                    Снять
                  </button>
                </div>
              </div>
              <div
                className="rounded-lg overflow-hidden"
                style={{ border: '1px solid var(--border)', maxHeight: 260, overflowY: 'auto' }}
              >
                {scanSources.length === 0 ? (
                  <div className="px-3 py-3 text-sm" style={{ color: 'var(--muted)' }}>
                    Нет источников для сканирования.
                  </div>
                ) : (
                  scanSources.map((source, idx) => {
                    const title = source.source_origin === 'sensor_managed'
                      ? 'Источник SENSOR'
                      : source.group_title ?? `ID ${source.chat_id}`
                    const subtitle = [
                      source.username ? `@${source.username.replace(/^@/, '')}` : null,
                      source.source_origin === 'sensor_managed' ? 'SENSOR' : null,
                      !source.last_scan_at ? 'без скана истории' : null,
                    ].filter(Boolean).join(' · ')
                    return (
                      <label
                        key={source.id}
                        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                        style={{ borderTop: idx > 0 ? '1px solid var(--border)' : undefined }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSourceIds.has(source.id)}
                          onChange={() => toggleSource(source.id)}
                          className="rounded"
                          style={{ accentColor: 'var(--teal)' }}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm truncate" style={{ color: 'var(--ink)' }}>
                            {title}
                          </span>
                          {subtitle && (
                            <span className="block text-xs" style={{ color: 'var(--muted)' }}>
                              {subtitle}
                            </span>
                          )}
                        </span>
                      </label>
                    )
                  })
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(false)}>
                Отмена
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={startScan.isPending || selectedSourceIds.size === 0}
                onClick={() => startScan.mutate()}
              >
                {startScan.isPending ? '...' : 'Запустить'}
              </button>
            </div>
            {startScan.isError && (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger-ink)' }}>
                {startScan.error instanceof ApiError
                  ? startScan.error.message
                  : 'Не удалось запустить скан. Попробуйте ещё раз.'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
